import DOMPurify from 'dompurify';

/**
 * Shared sanitizer for stored/AI-generated rich text rendered via
 * dangerouslySetInnerHTML (ICF/SAP section output, translations, version
 * history, amendment diff highlighting, appendix content). DOMPurify's hooks
 * are attached to its module-level singleton, so this file is the one place
 * that configures them — every DOMPurify.sanitize() call in the app,
 * including sanitizeSectionHtml.js, gets these safety checks for free.
 */

const ALLOWED_TAGS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'p', 'div', 'span', 'br', 'hr', 'pre', 'code',
  'ul', 'ol', 'li',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'strong', 'b', 'em', 'i', 'u', 's', 'mark', 'sub', 'sup',
  'a', 'img',
];

const ALLOWED_ATTR = ['style', 'class', 'href', 'src', 'alt', 'title', 'colspan', 'rowspan'];

// CSS values that can smuggle a network fetch or (in legacy engines) code
// execution through an otherwise-inert `style` attribute.
const UNSAFE_STYLE_PATTERN = /url\s*\(|expression\s*\(|@import|javascript:/i;

let hooksInstalled = false;
function installHooks() {
  if (hooksInstalled) return;
  hooksInstalled = true;

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A') {
      const href = node.getAttribute('href') || '';
      if (!/^(https?:|mailto:)/i.test(href)) {
        node.removeAttribute('href');
      } else {
        node.setAttribute('rel', 'noopener noreferrer');
        node.setAttribute('target', '_blank');
      }
    }
    if (node.tagName === 'IMG') {
      const src = node.getAttribute('src') || '';
      if (!/^(https?:|data:image\/)/i.test(src)) {
        node.removeAttribute('src');
      }
    }
    if (node.hasAttribute('style') && UNSAFE_STYLE_PATTERN.test(node.getAttribute('style') || '')) {
      node.removeAttribute('style');
    }
  });
}

/** Sanitize stored/AI-generated HTML before it reaches a dangerouslySetInnerHTML sink. */
export function sanitizeRichHtml(html) {
  if (!html) return '';
  installHooks();
  return DOMPurify.sanitize(String(html), {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
}
